import { useEffect, useState } from "react";
import type { MediaFile } from "../types/media";

// Define a constant for the storage key
export const FILE_CACHE_STORAGE_KEY = "senf-media-browser-file-cache";

export interface UseFileCacheReturn {
  fileCache: Record<string, MediaFile[]>;
  updateCachePath: (path: string, files: MediaFile[]) => void;
  addFilesToCache: (
    path: string,
    files: MediaFile[],
    sortFn?: (a: MediaFile, b: MediaFile) => number,
  ) => void;
  removeFilesFromCache: (path: string, fileIds: string[]) => void;
  clearCache: () => void;
}
export const getFileCache = (): Record<string, MediaFile[]> => {
  const cachedData = sessionStorage.getItem(FILE_CACHE_STORAGE_KEY);
  if (cachedData) {
    return JSON.parse(cachedData);
  }
  return {};
};

export const saveFileCache = (fileCache: Record<string, MediaFile[]>) => {
  try {
    sessionStorage.setItem(FILE_CACHE_STORAGE_KEY, JSON.stringify(fileCache));
  } catch (err) {
    // Handle potential quota exceeded errors or other storage issues
    console.warn("Failed to save file cache to sessionStorage:", err);

    // If it's a quota error, try to clear up some space by removing old entries
    if (err instanceof DOMException && err.name === "QuotaExceededError") {
      try {
        // Remove the oldest entries
        const oldCache = JSON.parse(
          sessionStorage.getItem(FILE_CACHE_STORAGE_KEY) || "{}",
        );
        const paths = Object.keys(oldCache);

        // If there are too many entries, remove the oldest ones
        if (paths.length > 10) {
          const reducedCache = {};
          for (const path of paths.slice(-5)) {
            reducedCache[path] = oldCache[path];
          }
          sessionStorage.setItem(
            FILE_CACHE_STORAGE_KEY,
            JSON.stringify(reducedCache),
          );
        }
      } catch (clearErr) {
        // If all else fails, clear storage and continue
        sessionStorage.removeItem(FILE_CACHE_STORAGE_KEY);
      }
    }
  }
};

// Add files to a specific path in the cache
export const addFilesToCacheRaw = (
  path: string,
  files: MediaFile[],
  sortFn?: (a: MediaFile, b: MediaFile) => number,
) => {
  console.log("addFilesToCacheRaw", path, files);
  const prev = getFileCache();
  const existingFiles = prev[path] || [];
  const newFiles = [...existingFiles, ...files];

  saveFileCache({
    ...prev,
    [path]: sortFn ? newFiles.sort(sortFn) : newFiles,
  });
};

export const clearFileCache = () => {
  sessionStorage.removeItem(FILE_CACHE_STORAGE_KEY);
};

export function useFileCache(): UseFileCacheReturn {
  const [fileCache, setFileCache] = useState<Record<string, MediaFile[]>>(
    () => {
      try {
        // Try to load from sessionStorage on component mount
        return getFileCache();
      } catch (err) {
        console.warn("Failed to load file cache from sessionStorage:", err);
      }
      return {};
    },
  );

  // Update a specific path in the cache with new files
  const updateCachePath = (path: string, files: MediaFile[]) => {
    setFileCache((prev) => ({
      ...prev,
      [path]: files,
    }));
  };

  // Add files to a specific path in the cache
  const addFilesToCache = (
    path: string,
    files: MediaFile[],
    sortFn?: (a: MediaFile, b: MediaFile) => number,
  ) => {
    setFileCache((prev) => {
      const existingFiles = prev[path] || [];
      const newFiles = [...existingFiles, ...files];

      return {
        ...prev,
        [path]: sortFn ? newFiles.sort(sortFn) : newFiles,
      };
    });
  };

  // Remove files from a specific path in the cache
  const removeFilesFromCache = (path: string, fileIds: string[]) => {
    setFileCache((prev) => {
      const updatedCache = { ...prev };
      if (updatedCache[path]) {
        updatedCache[path] = updatedCache[path].filter(
          (file) => !fileIds.includes(file.id),
        );
      }
      return updatedCache;
    });
  };

  // Clear the entire cache
  const clearCache = () => {
    setFileCache({});
    clearFileCache();
  };

  // Add an effect to store cache in sessionStorage when it changes
  useEffect(() => {
    saveFileCache(fileCache);
  }, [fileCache]);

  return {
    fileCache,
    updateCachePath,
    addFilesToCache,
    removeFilesFromCache,
    clearCache,
  };
}
